import React from 'react';
import { Export } from '@phosphor-icons/react';
import { useContextMenuPlugin } from '@chatsdet/ui';
import { useTypingEffect } from '../../../../hooks/useTypingEffect';
import { useExportFlowAsPng } from '../useExportFlowAsPng';

type UseWorkflowNamingParams = {
  workflowId: string | null;
  workflowName: string;
  isWorkflowLoading: boolean;
  generatedName: string | null;
  setGeneratedName: (name: string | null) => void;
  updateWorkflowMutation: { mutate: (data: { name: string }) => void };
};

export function useWorkflowNaming({
  workflowId,
  workflowName,
  isWorkflowLoading,
  generatedName,
  setGeneratedName,
  updateWorkflowMutation,
}: UseWorkflowNamingParams) {
  const { displayedText: typedName, isComplete: typingComplete } = useTypingEffect(
    generatedName || '',
    {
      speed: 20,
      autoStart: true,
      onComplete: async () => {
        if (generatedName && workflowId) {
          updateWorkflowMutation.mutate({ name: generatedName });
        }
      },
    }
  );

  const workflowCanvasTitle = React.useMemo(() => {
    if (!workflowId) {
      return '';
    }
    if (isWorkflowLoading) {
      return 'Loading flow\u2026';
    }
    if (generatedName) {
      return typedName;
    }
    return workflowName;
  }, [workflowId, isWorkflowLoading, generatedName, typedName, workflowName]);

  // Clean up generated name once typing completes and matches
  React.useEffect(() => {
    if (generatedName && typingComplete && workflowName === generatedName) {
      const timer = setTimeout(() => {
        setGeneratedName(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [generatedName, typingComplete, workflowName, setGeneratedName]);

  // Register "Export as PNG" context menu plugin
  const exportFlowAsPng = useExportFlowAsPng(workflowName);
  useContextMenuPlugin(
    React.useMemo(
      () => ({
        id: 'export-flow-png',
        label: 'Export as PNG',
        icon: <Export size={16} />,
        onAction: exportFlowAsPng,
      }),
      [exportFlowAsPng]
    )
  );

  return {
    typedName,
    typingComplete,
    workflowCanvasTitle,
  };
}
